import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addCart, delCart, closeCart } from "../redux/action";
import { useCurrency } from "../context/CurrencyContext";
import { WHATSAPP_PHONE } from "../config";
import { toAnalyticsItem, trackEvent } from "../lib/analytics";

const CartSidebar = () => {
  const cart = useSelector((state) => state.handleCart);
  const cartOpen = useSelector((state) => state.ui.cartOpen);
  const dispatch = useDispatch();

  useEffect(() => {
    const root = document.getElementById("root");
    const getDesktopSidebarWidth = () =>
      window.matchMedia("(min-width: 992px) and (max-width: 1280px)").matches
        ? 320
        : 360;

    const updateBodyScroll = () => {
      const isDesktop = window.matchMedia("(min-width: 992px)").matches;
      document.body.style.overflow = cartOpen && !isDesktop ? "hidden" : "";

      if (!root) return;
      root.style.transition = "padding-right 220ms ease";

      if (cartOpen && isDesktop) {
        root.style.paddingRight = `${getDesktopSidebarWidth()}px`;
      } else {
        root.style.paddingRight = "";
      }
    };

    updateBodyScroll();
    window.addEventListener("resize", updateBodyScroll);
    return () => {
      window.removeEventListener("resize", updateBodyScroll);
      document.body.style.overflow = "";
      if (root) {
        root.style.paddingRight = "";
        root.style.transition = "";
      }
    };
  }, [cartOpen]);

  useEffect(() => {
    if (cart.length === 0 && cartOpen) {
      dispatch(closeCart());
    }
  }, [cart.length, cartOpen, dispatch]);

  const { currency, formatPrice } = useCurrency();
  const subtotal = cart.reduce((sum, item) => sum + item.price * item.qty, 0);
  const totalItems = cart.reduce((sum, item) => sum + item.qty, 0);
  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  const whatsappMessage = [
    "A1 Pro - Nuevo pedido",
    "",
    "Resumen:",
    ...cart.map((item) => `- ${item.title} x${item.qty} (${formatPrice(item.price)})`),
    "",
    `Total productos: ${totalItems}`,
    `Moneda: ${currency}`,
    `Total: ${formatPrice(subtotal)}`,
    `URL: ${pageUrl}`,
  ].join("\n");
  const whatsappUrl = `https://api.whatsapp.com/send?phone=${WHATSAPP_PHONE}&text=${encodeURIComponent(
    whatsappMessage
  )}`;

  const close = () => dispatch(closeCart());

  const buildCheckoutPayload = () => ({
    currency,
    value: Number(subtotal || 0),
    items: cart.map((item) => toAnalyticsItem(item, item.qty)),
    item_count: Number(totalItems || 0),
  });

  const addItem = (item) => {
    trackEvent("add_to_cart", {
      currency,
      value: Number(item.price || 0),
      items: [toAnalyticsItem(item, 1)],
    });
    dispatch(addCart(item));
  };

  const removeItem = (item) => {
    trackEvent("remove_from_cart", {
      currency,
      value: Number(item.price || 0),
      items: [toAnalyticsItem(item, 1)],
    });
    dispatch(delCart(item));
  };

  if (cart.length === 0) {
    return null;
  }

  return (
    <>
      <div
        className={`cart-overlay${cartOpen ? " open" : ""}`}
        onClick={close}
        aria-hidden={!cartOpen}
      />
      <aside className={`cart-sidebar${cartOpen ? " open" : ""}`}>
        <div className="cart-sidebar-header">
          <div>
            <h5 className="mb-0">Carrito</h5>
            <small className="text-muted">{totalItems} productos</small>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-dark"
            onClick={close}
          >
            Cerrar
          </button>
        </div>

        <div className="cart-sidebar-body">
          {cart.map((item) => (
            <div key={item.id} className="cart-item mb-3">
              <img src={item.image} alt={item.title} />
              <div className="flex-grow-1">
                <div className="d-flex justify-content-between">
                  <strong className="me-2">{item.title}</strong>
                  <span>{formatPrice(item.price)}</span>
                </div>
                <div className="d-flex align-items-center gap-2 mt-2">
                  <button
                    className="btn btn-sm btn-outline-dark"
                    onClick={() => removeItem(item)}
                  >
                    -
                  </button>
                  <span>{item.qty}</span>
                  <button
                    className="btn btn-sm btn-outline-dark"
                    onClick={() => addItem(item)}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="cart-sidebar-footer">
          <div className="d-flex justify-content-between mb-3">
            <span>Total</span>
            <strong>{formatPrice(subtotal)}</strong>
          </div>
          <a
            href={whatsappUrl}
            className="btn btn-whatsapp w-100"
            onClick={() => {
              trackEvent("whatsapp_checkout", {
                ...buildCheckoutPayload(),
                source: "cart_sidebar",
              });
              close();
            }}
            target="_blank"
            rel="noreferrer"
          >
            Finalizar por WhatsApp
          </a>
          <Link
            to="/checkout"
            className="btn btn-dark w-100"
            onClick={() => {
              trackEvent("begin_checkout", buildCheckoutPayload());
              close();
            }}
          >
            Finalizar compra
          </Link>
          <Link
            to="/cart"
            className="btn btn-outline-dark w-100 mt-2"
            onClick={close}
          >
            Ver carrito completo
          </Link>
        </div>
      </aside>
    </>
  );
};

export default CartSidebar;
